import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService } from 'primeng/api';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

// Component imports
import { LearnMoreDialogComponent } from '../learn-more-dialog/learn-more-dialog.component';
import { LearnMoreItemComponent } from '../learn-more-item/learn-more-item.component';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner.component';

// API Service
import { LearnMoreApiService } from '../../services/learn-more-api.service';
import type { LearnMoreResponseAdminDto } from '../../api/models';

@Component({
  selector: 'app-learn-more',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    MessageModule,
    SkeletonModule,
    ToastModule,
    ConfirmDialogModule,
    LearnMoreItemComponent,
    LoadingSpinnerComponent,
  ],
  providers: [DialogService, MessageService, ConfirmationService],
  templateUrl: './learn-more.component.html',
  styleUrls: ['./learn-more.component.css'],
})
export class LearnMoreComponent implements OnInit, OnDestroy {
  // Injected services
  private readonly dialogService = inject(DialogService);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly apiService = inject(LearnMoreApiService);

  // Loading state
  loading = signal(true);

  learnMoreItems: LearnMoreResponseAdminDto[] = [];
  private ref: DynamicDialogRef | undefined;

  ngOnInit(): void {
    this.loadLearnMore();
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  /**
   * Load learn more resources from API
   */
  async loadLearnMore(): Promise<void> {
    this.loading.set(true);

    try {
      this.learnMoreItems = await this.apiService.listLearnMore();
    } catch (error) {
      console.error('Failed to load learn more resources:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('learnMore.errors.loadFailed'),
        life: 4000,
      });
    } finally {
      this.loading.set(false);
    }
  }

  openAddLearnMoreDialog(): void {
    this.ref = this.dialogService.open(LearnMoreDialogComponent, {
      header: this.translateService.instant('learnMore.addTitle'),
      width: '700px',
      modal: true,
      closable: true,
      focusOnShow: false,
    });

    this.ref.onClose.subscribe(async (result: any) => {
      if (result && result.success) {
        await this.loadLearnMore(); // Reload list

        // Show success toast
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('learnMore.success.addTitle'),
          detail: this.translateService.instant('learnMore.success.addMessage'),
          life: 4000,
        });
      }
    });
  }

  openEditLearnMoreDialog(item: LearnMoreResponseAdminDto, index: number): void {
    this.ref = this.dialogService.open(LearnMoreDialogComponent, {
      header: this.translateService.instant('learnMore.editTitle'),
      width: '700px',
      modal: true,
      closable: true,
      data: { learnMore: item },
    });

    this.ref.onClose.subscribe(async (result: any) => {
      if (result && result.success) {
        await this.loadLearnMore(); // Reload list

        // Show success toast
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('learnMore.success.editTitle'),
          detail: this.translateService.instant('learnMore.success.editMessage'),
          life: 4000,
        });
      }
    });
  }

  async removeLearnMoreItem(item: LearnMoreResponseAdminDto): Promise<void> {
    if (!item.id) return;

    // Show confirmation dialog
    this.confirmationService.confirm({
      message: this.translateService.instant('learnMore.confirmDelete.message', {
        title: item.title,
      }),
      header: this.translateService.instant('learnMore.confirmDelete.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('common.delete'),
      rejectLabel: this.translateService.instant('common.cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        try {
          await this.apiService.deleteLearnMore(item.id!);
          await this.loadLearnMore(); // Reload list

          // Show success toast
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('learnMore.success.removeTitle'),
            detail: this.translateService.instant('learnMore.success.removeMessage'),
            life: 4000,
          });
        } catch (error) {
          console.error('Failed to delete learn more resource:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.error'),
            detail: this.translateService.instant('learnMore.errors.deleteFailed'),
            life: 4000,
          });
        }
      },
    });
  }

  onEditItem(event: { item: LearnMoreResponseAdminDto; index: number }): void {
    this.openEditLearnMoreDialog(event.item, event.index);
  }

  onRemoveItem(event: { item: LearnMoreResponseAdminDto; index: number }): void {
    this.removeLearnMoreItem(event.item);
  }

  /**
   * Get full cover image URL
   */
  getCoverImageUrl(fileId?: string): string {
    if (!fileId) return '';
    return this.apiService.getCoverImageUrl(fileId);
  }
}
